import React, { ChangeEvent, ReactNode } from 'react';
import { TextField, Box, SxProps, Theme, IconButton, CSSProperties } from '@mui/material';
import { IconChevron, IconClose } from '../../assets/icons';
import {
  getTextFieldWithIconsSx,
  startAdornmentStyle,
  endAdornmentStyle,
  incrementDecrementContainerStyle,
  getButtonStyle,
  getClearButtonStyle,
  wrapperSx,
} from './input-mui.styled';

type InputMuiProps = {
  value: string;
  label?: string;
  placeholder?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  errorMessage?: string;
  type?: string;
  id?: string;
  name?: string;
  required?: boolean;
  autoFocus?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  step?: number;
  min?: number;
  max?: number;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClickInput?: () => void;
  variant?: 'outlined' | 'standard' | 'filled';
  disabledNegative?: boolean;
  sx?: SxProps<Theme>;
  labelSx?: CSSProperties;
  autoCapitalize?: string;
  clearEnabled?: boolean;
  reverse?: boolean;
};

export const InputMui: React.FC<InputMuiProps> = ({
  value,
  label,
  placeholder,
  onChange,
  disabled = false,
  errorMessage,
  type = 'text',
  id,
  name,
  required = false,
  autoFocus = false,
  readOnly = false,
  maxLength,
  step = 1,
  min,
  max,
  startIcon,
  endIcon,
  onKeyDown,
  clearEnabled = false,
  variant = 'outlined',
  disabledNegative = false,
  sx,
  labelSx,
  autoCapitalize,
  onClickInput,
  reverse,
}) => {
  const showClear = clearEnabled && value;
  const showIncrementDecrement = type === 'number';

  const getDecimalPlaces = (stepValue: number): number => {
    return (stepValue.toString().split('.')[1] || '').length;
  };

  const formatNumberValue = (numValue: number, decimalPlaces: number): string => {
    const fixed = numValue.toFixed(decimalPlaces);
    return parseFloat(fixed).toString();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange?.({ ...event, target: { ...event.target, value: event.target.value } } as ChangeEvent<HTMLInputElement>);
  };

  const handleIncrement = () => {
    const currentValue = parseFloat(value) || 0;
    const decimalPlaces = getDecimalPlaces(step);
    let newValue = (currentValue + step).toFixed(decimalPlaces);
    if (max !== undefined && parseFloat(newValue) > max) {
      newValue = max.toFixed(decimalPlaces);
    }
    onChange?.({
      target: { value: formatNumberValue(parseFloat(newValue), decimalPlaces) },
    } as ChangeEvent<HTMLInputElement>);
  };

  const handleDecrement = () => {
    const currentValue = parseFloat(value) || 0;
    const decimalPlaces = getDecimalPlaces(step);
    let newValue = (currentValue - step).toFixed(decimalPlaces);

    const minValue = min !== undefined ? min : disabledNegative ? 0 : -Infinity;
    if (parseFloat(newValue) < minValue) {
      newValue =
        minValue === -Infinity ? (currentValue - step).toFixed(decimalPlaces) : minValue.toFixed(decimalPlaces);
    }

    onChange?.({
      target: { value: formatNumberValue(parseFloat(newValue), decimalPlaces) },
    } as ChangeEvent<HTMLInputElement>);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (type === 'number' && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault();

      if (event.key === 'ArrowUp') handleIncrement();
      else handleDecrement();
    }

    onKeyDown?.(event);
  };

  const onClear = () => {
    onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
  };

  return (
    <Box sx={{ ...wrapperSx, ...(sx ? sx : {}) }}>
      <TextField
        id={id}
        name={name}
        type={type}
        value={value}
        label={label}
        placeholder={placeholder !== undefined ? placeholder : label}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        {...(onClickInput ? { onClick: onClickInput } : {})}
        disabled={disabled}
        error={!!errorMessage}
        helperText={errorMessage}
        required={required}
        autoFocus={autoFocus}
        variant={variant}
        autoCapitalize={autoCapitalize}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          readOnly,
          ...(maxLength && { maxLength }),
          ...(min !== undefined && { min }),
          ...(max !== undefined && { max }),
        }}
        sx={getTextFieldWithIconsSx(
          !!startIcon,
          !!endIcon,
          showIncrementDecrement,
          readOnly,
          !!onClickInput,
          reverse,
          labelSx,
        )}
        slotProps={{
          input: {
            startAdornment: startIcon ? <div style={startAdornmentStyle}>{startIcon}</div> : null,
            endAdornment:
              endIcon || showClear || showIncrementDecrement ? (
                <div style={endAdornmentStyle}>
                  {showIncrementDecrement && (
                    <div style={incrementDecrementContainerStyle}>
                      <IconButton
                        onClick={handleIncrement}
                        type="button"
                        disabled={disabled}
                        style={getButtonStyle(disabled)}
                      >
                        <IconChevron direction="up" size={20} />
                      </IconButton>
                      <IconButton
                        onClick={handleDecrement}
                        type="button"
                        disabled={disabled}
                        style={getButtonStyle(disabled)}
                      >
                        <IconChevron direction="down" size={20} />
                      </IconButton>
                    </div>
                  )}
                  {showClear && (
                    <IconButton
                      onClick={onClear}
                      type="button"
                      disabled={disabled}
                      style={getClearButtonStyle(disabled)}
                    >
                      <IconClose />
                    </IconButton>
                  )}
                  {endIcon}
                </div>
              ) : null,
          },
        }}
      />
    </Box>
  );
};
